import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'rideburaq_token';

const LostFound = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [rides, setRides] = useState([]);
  const [form, setForm] = useState({ reference: '', description: '', contactPhone: '' });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const token = localStorage.getItem(TOKEN_KEY);
  const authH = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = () => {
    fetch(`${API}/api/lost-items`, { headers: authH }).then((r) => r.ok ? r.json() : { lostItems: [] }).then((d) => setItems(d.lostItems || []));
    fetch(`${API}/api/rides`, { headers: authH }).then((r) => r.ok ? r.json() : []).then((d) => {
      const list = Array.isArray(d) ? d : (d.rides || []);
      setRides(list.filter((r) => ['completed', 'cancelled'].includes(r.status)));
    });
  };

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.reference || !form.description) { setError('Please pick a ride and describe the item.'); return; }
    setStatus('sending'); setError('');
    try {
      const res = await fetch(`${API}/api/rides/${form.reference}/lost-item`, {
        method: 'POST', headers: authH,
        body: JSON.stringify({ description: form.description, contactPhone: form.contactPhone }),
      });
      if (!res.ok) throw new Error('failed');
      setForm({ reference: '', description: '', contactPhone: '' });
      setStatus('done');
      load();
    } catch (err) { setStatus('error'); setError('Could not submit. Please try again.'); }
  };

  const badge = (s) => ({
    open: 'text-orange-300 bg-orange-500/10', found: 'text-green-300 bg-green-500/10',
    returned: 'text-green-300 bg-green-500/10', closed: 'text-gray-400 bg-white/5',
    not_found: 'text-gray-400 bg-white/5',
  }[s] || 'text-gray-400 bg-white/5');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 text-white">
      <div className="max-w-2xl mx-auto px-6 py-10">
        <button data-testid="lost-back-btn" onClick={() => navigate('/dashboard')} className="text-sm text-gray-400 hover:text-white mb-8">← Back</button>
        <p className="text-[11px] tracking-[0.35em] uppercase text-orange-400 mb-4">Lost &amp; Found</p>
        <h1 className="text-4xl font-medium leading-tight mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>Left something behind?</h1>

        <form onSubmit={submit} className="space-y-5 mb-12">
          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-2">Which ride?</label>
            <select data-testid="lost-ride" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-orange-500 focus:outline-none">
              <option value="" className="bg-gray-900">Select a recent ride…</option>
              {rides.map((r) => <option key={r.reference} value={r.reference} className="bg-gray-900">{r.reference} — {r.dropoff_name || r.dropoffName || 'ride'}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-2">What did you lose?</label>
            <textarea data-testid="lost-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-orange-500 focus:outline-none" placeholder="e.g. Black leather wallet, back seat" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-gray-400 mb-2">Best contact number (optional)</label>
            <input data-testid="lost-phone" value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-orange-500 focus:outline-none" placeholder="(555) 555-5555" />
          </div>
          {error && <p data-testid="lost-error" className="text-sm text-red-400">{error}</p>}
          {status === 'done' && <p data-testid="lost-success" className="text-sm text-green-400">Report submitted — our team will follow up.</p>}
          <button data-testid="lost-submit" type="submit" disabled={status === 'sending'} className="px-6 py-3 rounded-full bg-orange-500 text-black font-semibold hover:bg-orange-400 transition-colors disabled:opacity-60">
            {status === 'sending' ? 'Submitting…' : 'Report lost item'}
          </button>
        </form>

        <h2 className="text-lg mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Your reports</h2>
        {items.length === 0 ? (
          <p className="text-gray-500 text-sm" data-testid="lost-empty">No reports yet.</p>
        ) : (
          <div className="space-y-3" data-testid="lost-list">
            {items.map((it) => (
              <div key={it.id} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-300">{it.reference}</span>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${badge(it.status)}`}>{(it.status || 'open').replace('_', ' ')}</span>
                </div>
                <p className="text-sm text-gray-400">{it.description}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LostFound;
